import { CircleLoader, EmptyState, Space } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { useQueryLegalEntity } from '@webapp/platform/legal-entities/features/view-legal-entity-details/models'
import { LegalEntityDetailsView } from '@webapp/platform/legal-entities/features/view-legal-entity-details/views/LegalEntityDetailsView'
import messages from './messages'

export interface ILegalEntityDetailsPresenterProps {
  uuid: string
  onEdit: () => void
  onDisable: () => void
  onReactivate: () => void
}

export const LegalEntityDetailsPresenter = ({
  uuid,
  onEdit,
  onDisable,
  onReactivate,
}: ILegalEntityDetailsPresenterProps) => {
  const intl = useProcurifyIntl()
  const { entity, isLoading, isError } = useQueryLegalEntity(uuid)

  if (isLoading) {
    return (
      <Space justify='center'>
        <CircleLoader />
      </Space>
    )
  }

  // Only surface the error state when there's genuinely nothing to show. This
  // view reads the shared list cache, and the edit flow invalidates that key —
  // if the post-edit refetch fails transiently, React Query keeps the previous
  // data (entity stays populated) while flipping isError, and we keep rendering
  // it rather than blanking a still-valid page.
  if (isError && !entity) {
    return (
      <EmptyState
        context='information'
        header={intl.formatMessage(messages.loadErrorTitle)}
        content={intl.formatMessage(messages.loadErrorMessage)}
      />
    )
  }

  if (!entity) {
    return (
      <EmptyState
        context='information'
        header={intl.formatMessage(messages.notFoundTitle)}
        content={intl.formatMessage(messages.notFoundMessage)}
      />
    )
  }

  return (
    <LegalEntityDetailsView
      name={entity.name}
      isActive={entity.active}
      baseCurrencyName={entity.baseCurrencyName}
      externalId={entity.externalId}
      country={entity.country}
      onEdit={onEdit}
      onDisable={onDisable}
      onReactivate={onReactivate}
    />
  )
}

LegalEntityDetailsPresenter.displayName = 'LegalEntityDetailsPresenter'
